import ThemeTableSimple2 from 'assets/components/table/themes/Theme.Table.Simple.2';
import { getLocales } from 'assets/locales/Locale';
import { formatNumberWithSeparator } from 'assets/utils/data/Number';
import { isNil } from 'lodash';

type Props = {
  contract?: Model.IContract;
  contractRefund: Model.IContractRefund;
};

type SummaryRow = {
  label: string;
  valueAtSale?: number;
  valueRetained?: number;
  valueRetainedDisplay?: string;
  refundAmount?: number;
  isTotal?: boolean;
};

const formatCurrency = (value?: number) => (isNil(value) ? 'N/A' : `$${formatNumberWithSeparator(value)}`);

const formatDeduction = (value?: number) => (isNil(value) ? 'N/A' : `($${formatNumberWithSeparator(Math.abs(value))})`);

const ContractRefundPriceSummariesTable = ({ contract, contractRefund }: Props) => {
  const { lang } = getLocales();
  const refundConfigTemplate = contractRefund.refundConfigTemplateJson ?? contractRefund.refundConfigTemplate;
  const protectedAmount = refundConfigTemplate?.dealerProtectProfitAmount;
  const dealerProtectProfitExpirationDays = refundConfigTemplate?.dealerProtectProfitExpirationDays;
  const shouldShowDealerProfitSplit =
    !isNil(protectedAmount) &&
    !isNil(dealerProtectProfitExpirationDays) &&
    contractRefund.usedTermInDays >= dealerProtectProfitExpirationDays;
  const dealerProfitAfterProtection = isNil(protectedAmount)
    ? undefined
    : Math.max(contractRefund.dealerProfitValueAtSale - protectedAmount, 0);
  const proratedRetainedAmount = isNil(dealerProfitAfterProtection)
    ? undefined
    : dealerProfitAfterProtection * contractRefund.retainFactor;
  const dealerProfitRetainedSplitDisplay =
    shouldShowDealerProfitSplit && !isNil(proratedRetainedAmount)
      ? `${formatCurrency(protectedAmount)} + ${formatCurrency(proratedRetainedAmount)}`
      : undefined;

  const taxLabel = (amount?: number, tax?: number) => {
    if (!amount || tax === undefined || tax === null) return lang.tax.toUpperCase();

    const rate = Number(((Math.abs(tax) / Math.abs(amount)) * 100).toFixed(2));
    return `${lang.tax.toUpperCase()} (${contract.client?.taxExempt ? lang.exempt.toUpperCase() : rate}%)`;
  };

  const contractTaxLabel = (...rates: Array<number | undefined>) => {
    if (rates.some((rate) => rate === undefined || rate === null)) return lang.tax.toUpperCase();

    const rate = rates.reduce((total, current) => total + (current ?? 0), 0) * 100;
    return `${lang.tax.toUpperCase()} (${contract.client?.taxExempt ? lang.exempt.toUpperCase() : rate.toFixed(2)}%)`;
  };

  const rows: SummaryRow[] = [
    {
      label: lang.dealerCostAmount,
      valueAtSale: contract?.dealerCost,
      valueRetained: contractRefund.retainedDealerCostAmt,
      refundAmount: contractRefund.refundedDealerCostAmt,
    },
    {
      label: `${lang.dealerCost} ${contractTaxLabel(
        contract?.dealerCostProvincialSalesTax,
        contract?.dealerCostGstHst
      )}`,
      valueAtSale: contractRefund.dealerCostTaxAtSale,
      valueRetained: contractRefund.retainedDealerCostTaxAmt,
      refundAmount: contractRefund.refundedDealerCostTaxAmt,
    },
    {
      label: lang.dealerCostTotal,
      valueAtSale: contractRefund.dealerCostValueAtSale,
      valueRetained: contractRefund.retainedDealerCostTotal,
      refundAmount: contractRefund.refundedDealerCostTotal,
      isTotal: true,
    },
    {
      label: lang.dealerProfitAmount,
      valueAtSale: contract?.dealerProfit,
      valueRetained: contractRefund.retainedDealerProfitAmt,
      valueRetainedDisplay: dealerProfitRetainedSplitDisplay,
      refundAmount: contractRefund.refundedDealerProfitAmt,
    },
    {
      label: `${lang.dealerProfit} ${contractTaxLabel(
        contract?.dealerProfitProvincialSalesTax,
        contract?.dealerProfitGstHst
      )}`,
      valueAtSale: contractRefund.dealerProfitTaxAtSale,
      valueRetained: contractRefund.retainedDealerProfitTaxAmt,
      refundAmount: contractRefund.refundedDealerProfitTaxAmt,
    },
    {
      label: lang.dealerProfitTotal,
      valueAtSale: contractRefund.dealerProfitValueAtSale,
      valueRetained: contractRefund.retainedDealerProfitTotal,
      refundAmount: contractRefund.refundedDealerProfitTotal,
      isTotal: true,
    },
    {
      label: lang.retailAmount,
      valueAtSale: contract?.salePriceCAD,
      valueRetained: contractRefund.totalRetainedDealerAmt,
      refundAmount: contractRefund.totalRefundedDealerAmt,
    },
    {
      label: `${lang.retail} ${taxLabel(contract?.salePriceCAD, contractRefund.retailSalePriceTaxValueAtSale)}`,
      valueAtSale: contractRefund.retailSalePriceTaxValueAtSale,
      valueRetained: contractRefund.totalRetainedDealerTaxAmt,
      refundAmount: contractRefund.totalRefundedDealerTaxAmt,
    },
    {
      label: lang.retailSalePriceTotal,
      valueAtSale: contractRefund.retailSalePriceValueAtSale,
      valueRetained: contractRefund.retainedDealerTotal,
      refundAmount: contractRefund.refundedDealerTotal,
      isTotal: true,
    },
  ];

  return (
    <table className={ThemeTableSimple2}>
      <thead>
        <tr>
          <th></th>
          <th>{lang.valueAtSales}</th>
          <th>{lang.valueRetained}</th>
          <th>{lang.refundAmount}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <td style={row.isTotal ? { fontWeight: 'bold' } : undefined}>{row.label}</td>
            <td style={row.isTotal ? { fontWeight: 'bold' } : undefined}>{formatCurrency(row.valueAtSale)}</td>
            <td style={row.isTotal ? { fontWeight: 'bold' } : undefined}>
              {row.valueRetainedDisplay ?? formatCurrency(row.valueRetained)}
            </td>
            <td style={row.isTotal ? { fontWeight: 'bold' } : undefined}>{formatCurrency(row.refundAmount)}</td>
          </tr>
        ))}
        <tr>
          <td>
            <div></div>
          </td>
          <td>
            <div></div>
          </td>
          <td>
            <div></div>
          </td>
          <td>
            <div></div>
          </td>
        </tr>
        <tr>
          <td>{lang.totalDeductions}</td>
          <td></td>
          <td></td>
          <td>
            {formatDeduction((contractRefund.totalDeductions ?? 0) + (contractRefund.totalDeductionsTaxAmount ?? 0))}
          </td>
        </tr>
        <tr>
          <td style={{ fontWeight: 'bold' }}>{lang.totalCustomerRefund}</td>
          <td></td>
          <td></td>
          <td style={{ fontWeight: 'bold' }}>{formatCurrency(contractRefund.totalToBeRefunded)}</td>
        </tr>
      </tbody>
    </table>
  );
};

export default ContractRefundPriceSummariesTable;
